import { captureException, ErrorBoundary } from "@sentry/react";
import React, { useCallback, useMemo, useState } from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { axiosWithBearerV2, normalizeErrorDetail } from "../../api/utils";
import { isEmpty, isValidEmail } from "../../utils/utilityFunctions";
import { ErrorMsg } from "../AppMessages";
import { BusinessFunctionInput, GeographicalScopeInput, TextInput } from "./common";

export const SignUpForm = withRouter((props: RouteComponentProps) => {
  const { history } = props;

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [emailAddress, setEmailAddress] = useState("");
  const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([]);
  const [businessFunction, setBusinessFunction] = useState("");
  const [department, setDepartment] = useState("");
  const [showForm, setShowForm] = useState<boolean>(true);

  // GeographicalScopeInput (rendered below) already dispatches fetchGeoScopeData itself on
  // mount - this used to duplicate that exact dispatch here too. Two concurrent dispatches of the
  // same action both call getGeoScopeData() -> fetchDataV2 under the same URL-keyed cancel token,
  // so one of them cancels the other; the cancelled one's saga (fetchGeoScope in filterSaga.ts)
  // then throws evaluating "error" in null and logs "Failed to get GeoScope Data" without ever
  // setting geoScope in state if it's the one that keeps losing the race. Removed here so there's
  // only ever one dispatch, not because this specific line was more wrong than the child's.

  // Single source of truth for both the submit button's disabled state AND what the validation
  // toast actually says - this used to be a bare boolean with no way to tell the user WHICH
  // field(s) were the problem, just a generic "Form values not valid" regardless of whether one
  // field was empty or five were.
  const missingFields = useMemo(() => {
    const missing: string[] = [];
    if (isEmpty(firstName)) missing.push("First name");
    if (isEmpty(lastName)) missing.push("Last name");
    if (isEmpty(emailAddress)) missing.push("Email address");
    else if (!isValidEmail(emailAddress)) missing.push("Email address (invalid format)");
    if (selectedGroupIds.length === 0) missing.push("Geographical scope");
    if (isEmpty(businessFunction)) missing.push("Business function");
    if (isEmpty(department)) missing.push("Department");
    return missing;
  }, [firstName, lastName, emailAddress, selectedGroupIds, businessFunction, department]);

  const validForm = missingFields.length === 0;

  const handleBackToLoginClick = useCallback(
    () => () => {
      history.push({ pathname: "/login" });
    },
    [history]
  );

  const handleSubmit = useCallback(() => {
    if (missingFields.length > 0) {
      toast.error(`Please complete: ${missingFields.join(", ")}`);

      return;
    }

    // Structured fields (docs/needed-endpoints.md §5) - the backend now creates a
    // _user_signup_request row directly from these rather than just firing a formatted-HTML
    // email, so the old message/subject/domain body (still built from these same state values,
    // just packaged differently) no longer matches what it validates against; confirmed via a
    // real 422 naming exactly these 5 fields as missing. `group_ids` replaces the old free-text
    // `geo_scope` string - it's the real Group ids the user picked, required and non-empty
    // (422 otherwise); any id that isn't a real Group is a 400, nothing partial gets saved.
    const body = {
      first_name: firstName,
      last_name: lastName,
      email: emailAddress,
      business_function: businessFunction,
      department,
      group_ids: selectedGroupIds,
    };
    const url = "/authentication/signup";

    // Only hide the form (showing the thank-you screen) once the request actually succeeds - this
    // used to fire unconditionally, right here, before the request even started. Any failure (the
    // 422 above, a network blip, a 500) still hid the form immediately, and since there's no
    // button anywhere in the error view to bring it back, the only way to retry was reloading the
    // page - wiping every field, including a just-picked Geographical scope/Department, and
    // forcing the user to redo the whole form from scratch. A toast (matching the existing
    // validation-error pattern above) keeps the form - and everything already filled in - exactly
    // as the user left it, so a retry is just clicking Submit again.
    axiosWithBearerV2
      .post(url, body)
      .then((res) => {
        // Live response is 201 (docs/needed-endpoints.md §5), not the 200 this used to check for
        // back when this endpoint just fired an email - any 2xx counts as success.
        if (res.status < 200 || res.status >= 300) {
          toast.error("Something went wrong submitting your request. Please try again.");
          captureException(res);
          return;
        }
        setShowForm(false);
      })
      .catch((err) => {
        // axios's own default validateStatus rejects any non-2xx (this client sets no override -
        // see api/utils.ts), so this is the only branch a real 401/422/500 ever actually reaches;
        // the res.status check in the .then above never sees an error response in practice. This
        // used to always show the same hardcoded toast regardless of what the backend actually
        // said - e.g. a 422's own {loc, msg} validation detail (the real, specific reason the
        // signup was rejected, like "email: value is not a valid email address") was silently
        // dropped, only ever reaching the dev console/Sentry, never the user.
        const detail = err?.response?.data?.detail;
        toast.error(normalizeErrorDetail(detail) ?? "Something went wrong submitting your request. Please try again.");
        console.error(err);
        // A 409 here is always one of a handful of deliberate duplicate-signup guardrails
        // (cedar-backend's _validate_signup_request: an account already exists, a request is
        // already pending, or one was already approved) - ordinary outcomes of someone
        // resubmitting the form or already having an account, never an application defect. Same
        // reasoning api/utils.ts's buildErrorObj already applies to a 401 - reporting every one of
        // these to Sentry as an exception was just noise crowding out real bugs.
        if (err?.response?.status !== 409) {
          captureException(err);
        }
      });
  }, [missingFields, firstName, lastName, businessFunction, department, emailAddress, selectedGroupIds]);

  return (
    <ErrorBoundary fallback={<ErrorMsg class={"formError"} />}>
      <>
        <form
          id="login_form"
          className="login_form sign_up_form"
          name="login_form"
          method="POST"
          data-testid="signup_form"
          // Several inputs below still carry a `pattern` attribute - without this, an invalid
          // value in one of THOSE fields (e.g. a malformed email) silently blocked the browser's
          // own "submit" event from ever firing at all, showing only its own unstyled native
          // validation bubble instead of this form's own toast/missingFields feedback - while
          // every OTHER invalid field (no pattern set, e.g. an empty Department) went through this
          // form's own JS validation and toast normally. Two inconsistent error paths depending on
          // which field was wrong; this keeps everything on the one path.
          noValidate
          onSubmit={(evt) => {
            evt.preventDefault();
            handleSubmit();
          }}
        >
          <div className="text_area">
            <h3 className="login_head form_header_text" data-test-id="login_form--welcome">
              Sign up to CEDAR Home
            </h3>
            {showForm ? (
              <>
                <div className="form_elements">
                  <div className="row">
                    <TextInput
                      label="First name"
                      name="firstName"
                      id="firstName"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className={"sign_up_input"}
                      pattern="[a-zA-Z]+"
                    />
                    <TextInput
                      label="Last name"
                      name="lastName"
                      id="lastName"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className={"sign_up_input"}
                      pattern="[a-zA-Z]+"
                    />
                  </div>

                  <div className="row">
                    <TextInput
                      label="Email address"
                      name="email"
                      id="email"
                      value={emailAddress}
                      onChange={(e) => setEmailAddress(e.target.value)}
                      className={"sign_up_input"}
                      pattern="[a-z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{2,}$"
                    />
                    <GeographicalScopeInput selectedGroupIds={selectedGroupIds} setSelectedGroupIds={setSelectedGroupIds} />
                  </div>

                  <div className="row">
                    <BusinessFunctionInput businessFunction={businessFunction} setBusinessFunction={setBusinessFunction} />
                    <TextInput
                      label="Department"
                      name="department"
                      id="department"
                      value={department}
                      onChange={(e) => setDepartment(e.target.value)}
                      className={"sign_up_input"}
                      pattern="[a-zA-Z0-9\s]+"
                    />
                  </div>
                </div>
                <div className="button_area">
                  <input
                    className={`submit_btn ${!validForm ? "disabled" : ""}`}
                    type="submit"
                    value="Submit"
                    id="sign_up"
                    data-testid="sign_up_btn"
                    // Real disabled attribute, not just the "disabled" CSS class above (which only
                    // set pointer-events: none - it looked disabled and blocked mouse clicks, but
                    // pressing Enter in a text field still submitted the form regardless, bypassing
                    // it entirely). handleSubmit's own missingFields check stays too - Enter-key
                    // submission still reaches it directly rather than going through this button.
                    disabled={!validForm}
                  />
                </div>
              </>
            ) : (
              // Only reached once the request has actually succeeded (see handleSubmit) - a
              // failure now keeps the form visible and shows a toast instead, so there's no
              // longer a "something went wrong" version of this screen to show here.
              <div className="success_container">
                <p>
                  Thank your for signing up to CEDAR HOME! Your account creation request has been received and the CEDAR team will send you
                  a welcome email shortly.
                </p>
                <p>
                  Ensure you check your spam inbox in case the email has been sent there. If you haven&apos;t received the email after 2
                  business days, you can reach out to nissan@artefact.com and we&apos;ll respond asap.
                </p>
              </div>
            )}

            <p id="login_link" data-test-id="login_link" className="links back_button" onClick={handleBackToLoginClick()}>
              <span /> Back to login
            </p>
          </div>
        </form>
        <ToastContainer
          position="top-right"
          autoClose={4500}
          hideProgressBar={true}
          newestOnTop={true}
          closeOnClick={true}
          draggable={false}
          pauseOnHover={false}
        />
      </>{" "}
    </ErrorBoundary>
  );
});
